// PATH: src/pages/TrialDetailPage.tsx
import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Divider,
  FormControlLabel,
  LinearProgress,
  Skeleton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBackRounded";
import ExpandMoreIcon from "@mui/icons-material/ExpandMoreRounded";
import DoneAllIcon from "@mui/icons-material/DoneAllRounded";
import DeleteIcon from "@mui/icons-material/DeleteOutlineRounded";
import { useCurriculumApi, useTrialsApi } from "../api";
import type { FactPromptsResponse, TrialApplyResponse, TrialDetail, TrialResolved, TrialRole } from "../models/domain";
import { PageHeader, StatusPill, errorMessage, useConfirm, useToast } from "../components/ui";
import FactSideView from "../components/trials/FactSideView";
import { SentInstructions } from "../components/settings/InstructionChain";
import { embedRolePrompts, versionsUsed } from "../components/settings/rolePrompts";
import { useEmbedPrompts } from "../components/settings/useEmbedPrompts";
import {
  OBJECTIVE_STATE,
  ROLE_LABELS,
  RUN_COUNTERS,
  TRIAL_POLL_MS,
  canApply,
  canDiscard,
  formatCount,
  formatUsd,
  groupObjectives,
  objectiveState,
  originLabel,
  staleReasonLabel,
  subjectOptions,
  sumRunResults,
  trialStatus,
} from "../components/trials/trialLogic";

const when = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" }) : "—";

/**
 * Case « enregistrer ces réglages pour la matière » de la confirmation : la
 * boîte de confirmation garde le nœud tel quel, la case tient donc son propre
 * état et le recopie dans une référence lue à la validation.
 */
const SaveSettingsBox: React.FC<{ valueRef: React.MutableRefObject<boolean> }> = ({ valueRef }) => {
  const [checked, setChecked] = React.useState(valueRef.current);
  return (
    <FormControlLabel
      control={
        <Checkbox
          checked={checked}
          onChange={(e) => {
            valueRef.current = e.target.checked;
            setChecked(e.target.checked);
          }}
        />
      }
      label={<Typography variant="body2">Enregistrer aussi ces réglages pour la matière</Typography>}
    />
  );
};

/** Les réglages qu'un essai a utilisés, avec l'origine de chaque valeur. */
const ResolvedSummary: React.FC<{ resolved: TrialResolved }> = ({ resolved }) => (
  <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" } }}>
    {(["fact_extraction", "fact_review"] as TrialRole[]).map((role) => {
      const r = resolved[role];
      const off = role === "fact_review" && r?.enabled === false;
      return (
        <Box key={role} sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2">{ROLE_LABELS[role]}</Typography>
          {off ? (
            <Typography variant="body2" color="error">
              Coupée pour l'essai ({originLabel(r?.origin?.enabled)})
            </Typography>
          ) : (
            <>
              <Typography variant="body2">
                <span className="mono">{r?.model ?? "—"}</span>{" "}
                <Typography component="span" variant="caption" color="text.secondary">
                  ({originLabel(r?.origin?.model)})
                </Typography>
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                {r?.instructions ? `Consigne : ${originLabel(r.origin?.instructions)}, ci-dessous` : "Aucune consigne"}
              </Typography>
            </>
          )}
        </Box>
      );
    })}
  </Box>
);

/**
 * Ce que chaque agent a reçu, dans l'ordre d'envoi : la version du prompt de
 * base, puis la consigne utilisée. Seule l'extraction enregistre sa version
 * (sur chaque unité) ; celle de la relecture n'est pas conservée par l'essai.
 */
const SentPrompts: React.FC<{ detail: TrialDetail; prompts: FactPromptsResponse | null; error: string | null }> = ({
  detail,
  prompts,
  error,
}) => {
  const resolved = detail.trial.resolved ?? {};
  const used: Record<TrialRole, string[]> = {
    fact_extraction: versionsUsed((detail.objectives ?? []).map((o) => o.trial?.prompt_version)),
    fact_review: [],
  };
  return (
    <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" } }}>
      {(["fact_extraction", "fact_review"] as TrialRole[]).map((role) => (
        <Box key={role} sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
            {ROLE_LABELS[role]}
          </Typography>
          {role === "fact_review" && resolved.fact_review?.enabled === false ? (
            <Typography variant="body2" color="text.secondary">
              Relecture coupée : rien n'a été envoyé.
            </Typography>
          ) : (
            <SentInstructions
              prompts={embedRolePrompts(role, prompts, error)}
              used={used[role]}
              instructions={resolved[role]?.instructions}
              origin={resolved[role]?.origin?.instructions}
            />
          )}
        </Box>
      ))}
    </Box>
  );
};

/**
 * Détail d'un essai : où il en est, ce qu'il a coûté, et objectif par
 * objectif les faits servis aujourd'hui face aux faits de l'essai, avec les
 * verdicts du relecteur. Puis « Appliquer » (les unités encore valables
 * remplacent les faits servis) ou « Jeter ».
 */
const TrialDetailPage: React.FC = () => {
  const { trialId = "" } = useParams<{ trialId: string }>();
  const navigate = useNavigate();
  const { getTrial, applyTrial, discardTrial } = useTrialsApi();
  const { getCurriculumOverview } = useCurriculumApi();
  const toast = useToast();
  const confirm = useConfirm();

  const [detail, setDetail] = React.useState<TrialDetail | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [subjectName, setSubjectName] = React.useState<string>("");
  const [busy, setBusy] = React.useState(false);
  const [applied, setApplied] = React.useState<TrialApplyResponse | null>(null);
  const [onlyChanges, setOnlyChanges] = React.useState(false);
  const saveSettings = React.useRef(false);
  const basePrompts = useEmbedPrompts();

  const load = React.useCallback(async () => {
    try {
      const { data } = await getTrial(trialId);
      setDetail(data);
      setError(null);
    } catch (e) {
      setError(errorMessage(e, "Impossible de charger l'essai."));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trialId]);

  React.useEffect(() => {
    setDetail(null);
    setApplied(null);
    void load();
  }, [load]);

  const subjectId = detail?.trial.subject_id;
  React.useEffect(() => {
    if (!subjectId) return;
    getCurriculumOverview()
      .then(({ data }) => setSubjectName(subjectOptions(data.programs ?? []).find((s) => s.id === subjectId)?.name ?? ""))
      .catch(() => setSubjectName(""));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subjectId]);

  const running = detail?.trial.status === "running";
  React.useEffect(() => {
    if (!running) return;
    const id = window.setInterval(() => void load(), TRIAL_POLL_MS);
    return () => window.clearInterval(id);
  }, [running, load]);

  const groups = React.useMemo(
    () => (detail ? groupObjectives(detail.documents, detail.objectives ?? []) : []),
    [detail],
  );
  const totals = React.useMemo(() => (detail ? sumRunResults(detail.documents) : null), [detail]);
  const objectiveText = React.useMemo(
    () => new Map((detail?.objectives ?? []).map((o) => [o.objective_id, `${o.ordinal}. ${o.text}`])),
    [detail],
  );
  const docTitle = React.useMemo(() => new Map((detail?.documents ?? []).map((d) => [d.document_id, d.title])), [detail]);

  const apply = async () => {
    if (!detail) return;
    saveSettings.current = false;
    const ok = await confirm({
      title: "Appliquer l'essai ?",
      message: (
        <Stack spacing={1.5}>
          <Typography variant="body2" color="text.secondary">
            Les faits de l'essai remplacent les faits servis aux élèves, objectif par objectif. Les décisions des éditeurs
            (faits désactivés, notes) sont reportées sur les nouveaux faits. Un objectif dont le cours a bougé depuis
            l'essai n'est pas appliqué : il sera listé.
          </Typography>
          <SaveSettingsBox valueRef={saveSettings} />
          <Typography variant="caption" color="text.secondary">
            Enregistrer les réglages en fait le réglage de la matière pour les prochaines extractions. L'interrupteur de
            relecture, lui, reste global.
          </Typography>
        </Stack>
      ),
      confirmLabel: "Appliquer",
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      const { data } = await applyTrial(detail.trial.id, saveSettings.current);
      setApplied(data);
      toast.success(`${data.applied} unité${data.applied > 1 ? "s" : ""} appliquée${data.applied > 1 ? "s" : ""}.`);
      await load();
    } catch (e) {
      toast.error(errorMessage(e, "Échec de l'application."));
      await load();
    } finally {
      setBusy(false);
    }
  };

  const discard = async () => {
    if (!detail) return;
    const ok = await confirm({
      title: "Jeter l'essai ?",
      message:
        (detail.trial.status === "running" ? "L'essai sera arrêté, puis jeté. " : "") +
        "Ses faits sont abandonnés ; les faits servis ne changent pas. Le coût déjà engagé reste dépensé.",
      confirmLabel: "Jeter",
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      await discardTrial(detail.trial.id);
      toast.success("Essai jeté.");
      await load();
    } catch (e) {
      toast.error(errorMessage(e, "Impossible de jeter l'essai."));
    } finally {
      setBusy(false);
    }
  };

  const back = (
    <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/trials")}>
      Essais
    </Button>
  );

  if (error && !detail) {
    return (
      <Box sx={{ maxWidth: 1280 }}>
        <PageHeader title="Essai d'extraction" actions={back} />
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => void load()}>
              Réessayer
            </Button>
          }
        >
          {error}
        </Alert>
      </Box>
    );
  }

  if (!detail) {
    return (
      <Box sx={{ maxWidth: 1280 }}>
        <PageHeader title="Essai d'extraction" actions={back} />
        <Card sx={{ p: 2.5 }}>
          <Skeleton width="40%" height={32} />
          <Skeleton height={56} />
          <Skeleton height={56} />
        </Card>
      </Box>
    );
  }

  const { trial, cost } = detail;
  const status = trialStatus(trial.status);
  const total = trial.document_ids.length;

  return (
    <Box sx={{ maxWidth: 1280 }}>
      <PageHeader
        title="Essai d'extraction"
        subtitle={`${subjectName || "Matière"} · lancé le ${when(trial.created_at)} par ${trial.created_by}`}
        actions={
          <>
            {back}
            {canDiscard(trial.status) && (
              <Button variant="outlined" color="error" startIcon={<DeleteIcon />} disabled={busy} onClick={() => void discard()}>
                Jeter
              </Button>
            )}
            {canApply(trial.status) && (
              <Button variant="contained" startIcon={<DoneAllIcon />} disabled={busy} onClick={() => void apply()}>
                Appliquer
              </Button>
            )}
          </>
        }
      />

      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {applied && (
        <Alert severity={applied.stale.length ? "warning" : "success"} sx={{ mb: 2 }} onClose={() => setApplied(null)}>
          <Typography variant="body2">
            <b>{applied.applied}</b> unité{applied.applied > 1 ? "s" : ""} appliquée{applied.applied > 1 ? "s" : ""}
            {applied.settings_saved ? " ; réglages enregistrés pour la matière." : "."}
          </Typography>
          {applied.settings_note && (
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {applied.settings_note}
            </Typography>
          )}
          {applied.stale.length > 0 && (
            <>
              <Typography variant="body2" sx={{ mt: 1 }}>
                {applied.stale.length} objectif{applied.stale.length > 1 ? "s" : ""} non appliqué
                {applied.stale.length > 1 ? "s" : ""} (relancez un essai pour eux) :
              </Typography>
              <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
                {applied.stale.map((s) => (
                  <li key={s.objective_id}>
                    <Typography variant="body2" component="span" dir="auto">
                      {objectiveText.get(s.objective_id) ?? s.objective_id}
                    </Typography>
                    <Typography variant="body2" component="span" color="text.secondary">
                      {" "}
                      ({docTitle.get(s.document_id) ?? "document"}) : {staleReasonLabel(s.reason)}
                    </Typography>
                  </li>
                ))}
              </Box>
            </>
          )}
        </Alert>
      )}

      <Card>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={{ xs: 2, md: 4 }}
          sx={{ p: { xs: 2, md: 2.5 } }}
          divider={<Divider orientation="vertical" flexItem />}
        >
          <Box sx={{ minWidth: 180 }}>
            <Typography variant="caption" color="text.secondary">
              Statut
            </Typography>
            <Box sx={{ mt: 0.5 }}>
              <StatusPill tone={status.tone}>{status.label}</StatusPill>
            </Box>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {trial.documents_done} / {total} document{total > 1 ? "s" : ""} traité{trial.documents_done > 1 ? "s" : ""}
            </Typography>
            {running && <LinearProgress variant="determinate" value={total ? (100 * trial.documents_done) / total : 0} sx={{ mt: 0.75 }} />}
            {trial.finished_at && (
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                fini le {when(trial.finished_at)}
              </Typography>
            )}
            {trial.applied_at && (
              <Typography variant="caption" color="text.secondary" component="div">
                appliqué le {when(trial.applied_at)} par {trial.applied_by ?? "—"}
              </Typography>
            )}
          </Box>
          <Box sx={{ minWidth: 200 }}>
            <Typography variant="caption" color="text.secondary">
              Coût
            </Typography>
            <Typography variant="h4" component="div">
              {formatUsd(cost?.usd ?? trial.actual_usd)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              réel, {formatCount(cost?.calls)} appel{(cost?.calls ?? 0) > 1 ? "s" : ""}
              {running ? " jusqu'ici" : ""} · estimé {formatUsd(trial.estimate_usd)}
            </Typography>
            {(cost?.unpriced_calls ?? 0) > 0 && (
              <Typography variant="caption" color="warning.dark" component="div">
                {cost.unpriced_calls} appel{cost.unpriced_calls > 1 ? "s" : ""} sans tarif : coût réel sous-estimé.
              </Typography>
            )}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
              Réglages utilisés
            </Typography>
            <ResolvedSummary resolved={trial.resolved ?? {}} />
          </Box>
        </Stack>
        {trial.error && (
          <Alert severity="error" sx={{ mx: { xs: 2, md: 2.5 }, mb: 2 }}>
            {trial.error}
          </Alert>
        )}
      </Card>

      <Card sx={{ mt: 2 }}>
        <Box sx={{ p: { xs: 2, md: 2.5 } }}>
          <Typography variant="h5">Ce que les agents ont reçu</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Dans l'ordre d'envoi : le prompt de base, puis la consigne en fin de message.
          </Typography>
          <SentPrompts detail={detail} prompts={basePrompts.prompts} error={basePrompts.error} />
        </Box>
      </Card>

      <Card sx={{ mt: 2 }}>
        <Box sx={{ p: { xs: 2, md: 2.5 }, pb: 1 }}>
          <Typography variant="h5">Par document</Typography>
        </Box>
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Document</TableCell>
                {RUN_COUNTERS.map((c) => (
                  <TableCell key={c.key} align="right">
                    {c.label}
                  </TableCell>
                ))}
                <TableCell align="right">Passages en échec</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {detail.documents.map((d) => (
                <TableRow key={d.document_id}>
                  <TableCell sx={{ minWidth: 180 }}>
                    <Typography variant="body2" dir="auto">
                      {d.title}
                    </Typography>
                    {d.error && (
                      <Typography variant="caption" color="error" component="div">
                        {d.error}
                      </Typography>
                    )}
                    {!d.result && !d.error && (
                      <Typography variant="caption" color="text.secondary" component="div">
                        {running ? "en attente" : "aucun résultat"}
                      </Typography>
                    )}
                  </TableCell>
                  {RUN_COUNTERS.map((c) => (
                    <TableCell key={c.key} align="right">
                      {d.result ? formatCount(d.result[c.key]) : "—"}
                    </TableCell>
                  ))}
                  <TableCell align="right">
                    {d.result ? `${formatCount(d.result.failed_passages)} / ${formatCount(d.result.passages)}` : "—"}
                  </TableCell>
                </TableRow>
              ))}
              {totals && detail.documents.length > 1 && (
                <TableRow sx={{ "& td": { fontWeight: 650 } }}>
                  <TableCell>Total</TableCell>
                  {RUN_COUNTERS.map((c) => (
                    <TableCell key={c.key} align="right">
                      {formatCount(totals[c.key])}
                    </TableCell>
                  ))}
                  <TableCell align="right">
                    {formatCount(totals.failed_passages)} / {formatCount(totals.passages)}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <Box sx={{ mt: 3, mb: 1 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="space-between" alignItems={{ sm: "center" }}>
          <Box>
            <Typography variant="h5">Par objectif</Typography>
            <Typography variant="body2" color="text.secondary">
              À gauche les faits servis aujourd'hui, à droite ceux de l'essai avec les verdicts de son relecteur. Un fait
              barré est écarté.
            </Typography>
          </Box>
          <FormControlLabel
            control={<Switch checked={onlyChanges} onChange={(e) => setOnlyChanges(e.target.checked)} />}
            label={<Typography variant="body2">Seulement les objectifs qui changent</Typography>}
          />
        </Stack>
      </Box>

      {groups.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          {running ? "Les objectifs apparaissent au fil de l'essai." : "Aucun objectif dans cet essai."}
        </Typography>
      )}

      {groups.map((g, i) => {
        const rows = onlyChanges ? g.objectives.filter((o) => objectiveState(o) !== "same") : g.objectives;
        return (
          <Accordion key={g.document_id || "autres"} defaultExpanded={i === 0} disableGutters sx={{ mb: 1 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap" useFlexGap>
                <Typography variant="subtitle1" dir="auto">
                  {g.title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {g.objectives.length} objectif{g.objectives.length > 1 ? "s" : ""}
                  {onlyChanges ? `, ${rows.length} qui change${rows.length > 1 ? "nt" : ""}` : ""}
                </Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              {rows.map((o) => {
                const state = OBJECTIVE_STATE[objectiveState(o)];
                return (
                  <Box key={o.objective_id} sx={{ borderTop: 1, borderColor: "divider", px: { xs: 2, md: 2.5 }, py: 2 }}>
                    <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
                      <Typography variant="body1" sx={{ fontWeight: 600 }} dir="auto">
                        {o.ordinal}. {o.text}
                      </Typography>
                      <StatusPill tone={state.tone}>{state.label}</StatusPill>
                    </Stack>
                    <Box sx={{ display: "grid", gap: { xs: 2, md: 3 }, gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" } }}>
                      <FactSideView title="Servi aujourd'hui" side={o.served} emptyLabel="Aucun fait servi aujourd'hui." />
                      <FactSideView
                        title="Essai"
                        side={o.trial}
                        emptyLabel={o.failed ? "L'extraction de l'essai a échoué pour cet objectif." : "L'essai n'a rien extrait."}
                      />
                    </Box>
                  </Box>
                );
              })}
              {rows.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ px: 2.5, py: 2 }}>
                  Aucun objectif ne change dans ce document.
                </Typography>
              )}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
};

export default TrialDetailPage;
